import _ from "lodash";
import { useCallback, useMemo } from "react";

import { GridSortDirection } from "@mui/x-data-grid";

import industries from "@components/FilterControls/utilities/Industries";

import { CompanyType } from "@interfaces/company.type";

import { ICompanyRowData } from "@pages/Companies/components/useEmbeddingTableConfig";
import embeddingSearchSpecificColumns from "@pages/Companies/configs/embeddingSearchSpecificColumns";
import generateCompanyColumns from "@pages/Companies/configs/generateCompanyColumns";
import { addIndustryToFilter, handleRowFocus } from "@pages/Companies/utils";

import { useTableStore } from "@store/filters/filters.store";

interface ITechnologyCompanyTableConfig {
  companies: CompanyType[];
  rowMenuItems?: { func: (companyId: string) => void; label: string }[];
  setFocusedId: (id: string) => void;
}

const useTechnologyCompanyTableConfig = ({ companies, rowMenuItems, setFocusedId }: ITechnologyCompanyTableConfig) => {
  const { filters, pagination, selection, setTableFilters, sort } = useTableStore("technologyCompany");

  const companiesContainScores = useMemo(() => _.some(companies, "similarityScore"), [companies]);

  const handleAddIndustryToFilter = useCallback(
    (newIndustry: string) => {
      addIndustryToFilter({
        filtersObject: {
          filters,
          pagination,
          selection,
          sort,
        },
        handleSetFilters: setTableFilters,
        industries,
        newIndustry,
      });
    },
    [filters, setTableFilters],
  );

  const columns = generateCompanyColumns({
    addIndustryToFilter: handleAddIndustryToFilter,
    filtersObject: {
      filters,
      pagination,
      selection,
      sort,
    },
    rowMenuItems,
  });

  const updatedColumns = [
    ...columns.slice(0, 4),
    ...(companiesContainScores ? embeddingSearchSpecificColumns : []),
    ...columns.slice(4),
  ];

  const rows: ICompanyRowData[] = useMemo(
    () =>
      (companies || []).map((company) => {
        return {
          companyId: company.id,
          country: company.country || null,
          hasLicensed: company.hasSec,
          hasSponsored: company.hasSponsor,
          id: company.id,
          key: company.id,
          logo: { alt: company.name || "", url: company.logoUrl || "" },
          name: company.name || "",
          numberPatents: company.numberMatchedPatents || "-",
          numEmployees: company.estimatedNumEmployees || "-",
          primaryDomain: company.primaryDomain,
          rowActions: company.id || null,
          sector: company.industries ?? [],
          similarityScore: company.patentSimilarityScore || null,
        };
      }),
    [companies],
  );

  const initialState = {
    pagination: { paginationModel: { page: 0, pageSize: 20 } },
    sorting: {
      sortModel: [
        { field: companiesContainScores ? "similarityScore" : "numEmployees", sort: "desc" as GridSortDirection },
      ],
    },
  };

  return {
    columns: updatedColumns,
    handleRowFocus: handleRowFocus({ companies, setFocusedId }),
    initialState,
    rows,
  };
};

export default useTechnologyCompanyTableConfig;
